import classNames from 'classnames';
import {Button, ButtonProps} from 'antd';

import useCommon from '@utils/hooks/common';
import useIsMobile from '@utils/hooks/useIsMobile';
import styles from './styles';
import CustomPopup, {CustomPopupProps} from '@components/CustomPopup';
import CustomModalSheet, {CustomModalSheetProps} from '@components/CustomModalSheet';

interface PopupContainerProps {
  visible: boolean;
  onCancel: () => void;
  onBack?: () => void;
  children: any;

  desktopModalProps?: Omit<CustomPopupProps, 'visible' | 'onCancel'>;
  mobileModalProps?: Omit<CustomModalSheetProps, 'isModalSheetOpen' | 'onModalSheetClose'>;
  disableScrollLocking?: boolean;
  sheetDetent?: 'full-height' | 'content-height';
  title?: JSX.Element | string;
  closable?: boolean;
}

const PopupContainer = (props: PopupContainerProps) => {
  const {classes, theme} = useCommon({
    withStyles: styles,
    observers: ['user'],
  });

  const {
    visible,
    onCancel,
    onBack,
    children,
    desktopModalProps,
    mobileModalProps,
    disableScrollLocking = true,
    sheetDetent = 'full-height',
    title,
    closable = true,
  } = props;

  // Some flows (e.g. 88x merchant iframe) force the desktop theme even when
  // the actual viewport is narrow. A 600px CustomPopup on a ~375px viewport
  // is effectively invisible — overflows the screen and never reaches the
  // user. Fall back to the mobile bottom sheet whenever the viewport itself
  // is mobile-sized, regardless of theme.
  const isMobileViewport = useIsMobile();
  const isDesktop = !isMobileViewport && theme?.currentStyleFormat?.id === 'desktop';

  if (isDesktop) {
    return (
      <CustomPopup
        title={title}
        width={600}
        open={visible}
        closable={closable}
        onClose={closable && onCancel}
        onCancel={onCancel}
        onBack={onBack}
        styles={{
          body: {
            overflow: 'auto',
          },
        }}
        bodyStyle={{
          height: 1, // To let children 100% actually work
          minHeight: 700,
          ...desktopModalProps?.bodyStyle,
        }}
        {...desktopModalProps}
      >
        {children}
      </CustomPopup>
    );
  }

  return (
    <CustomModalSheet
      sheetDetent={sheetDetent}
      isModalSheetOpen={visible}
      onModalSheetClose={onCancel}
      onModalSheetBack={onBack}
      disableScrollLocking={disableScrollLocking}
      title={title}
      closable={closable}
      {...mobileModalProps}
    >
      {children}
    </CustomModalSheet>
  );
};

export default PopupContainer;
